import React, { useState, useRef, useContext } from "react"
import { validateFunc } from "../../constraints/constraints"
import { useTranslation } from "react-i18next"
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Input,
  Row,
  Col,
  Alert,
} from "reactstrap"
import { createFoodMaster, editFoodMaster } from "../../apollo/server"
import { getIsDisabled } from "../../utils/getIsDisabled"
import { useMutation, gql, } from "@apollo/client"
import { zenkakuToHankaku } from "../../utils/transformZenkaku"
import { getCategoryMasterQuery, useCategoryMasterQuery } from "../../domain/graphql/query/useCategoryMasterQuery"
import { AppContext } from "../../app"

const CREATE_FOOD = gql`${createFoodMaster}`
const EDIT_FOOD = gql`${editFoodMaster}`

interface Props {
  food?: any
  ownerId: string
  modalLoading: boolean
  setModalLoading: React.Dispatch<React.SetStateAction<boolean>>
}
const FoodMasterModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const userType = appContext.userType
  const formRef = useRef(null)
  const mutation = props.food ? EDIT_FOOD : CREATE_FOOD

  const [title, setTitle] = useState(props.food && props.food.title ? props.food.title : "")
  const [description, setDescription] = useState(props.food && props.food.description ? props.food.description : "")
  const [categoryId, setCategoryId] = useState(props.food && props.food.categoryId ? props.food.categoryId : "")
  const [imgUrl, setImgUrl] = useState(props.food && props.food.image ? props.food.image : "")
  const [imgFile, setImgFile] = useState(null)

  const [mainError, mainErrorSetter] = useState("")
  const [success, successSetter] = useState("")

  const [titleError, titleErrorSetter] = useState(null)
  const [categoryError, categoryErrorSetter] = useState(null)

  const [variation, variationSetter] = useState(
    props.food && props.food.variations
      ? (
        props.food.variations.map(({ _id, title, storeProfit, janCode, }) => {
          return {
            _id,
            title,
            storeProfit,
            janCode,
            titleError: null,
          }
        })
      ) : (
        [
          {
            title: "",
            storeProfit: "",
            janCode: "",
            titleError: null,
          }
        ]
      )
  )
  const filterImage = event => {
    let images = []
    for (var i = 0; i < event.target.files.length; i++) {
      images[i] = event.target.files.item(i)
    }
    images = images.filter(image => image.name.match(/\.(jpg|jpeg|png|gif)$/))
    const message = `${images.length} valid image(s) selected`
    console.log(message)
    return images.length ? images[0] : undefined
  }
  const selectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const image = filterImage(event)
    if (image) {
      setImgFile(image)
      imageToBase64(image)
    }
  }

  const onAdd = (index: number) => {
    const variations = variation
    if (index === variations.length - 1) {
      variations.push({
        title: "",
        storeProfit: "",
        janCode: "",
        titleError: null,
      })
    } else {
      variations.splice(index + 1, 0, {
        title: "",
        storeProfit: "",
        janCode: "",
        titleError: null,
      })
    }
    variationSetter([...variations])
  }
  const onRemove = (index: number) => {
    if (variation.length === 1 && index === 0) {
      return
    }
    const variations = variation
    variations.splice(index, 1)
    variationSetter([...variations])
  }
  const handleVariationChange = (event, index, type) => {
    const variations = variation

    if (type === "title") {
      variations[index][type] = event.target.value
      variationSetter([...variations])
    } else {
      variations[index][type] = event.target.value
      variationSetter([...variations])
    }
  }
  const onSubmitValidaiton = () => {
    const titleError = !validateFunc({ title: title.trim() }, "title")
    const categoryError = !validateFunc({ category: categoryId.trim() }, "category")
    titleErrorSetter(titleError)
    categoryErrorSetter(categoryError)
    return titleError && categoryError
  }

  const clearFields = () => {
    formRef.current.reset()
    setTitle("")
    setDescription("")
    setCategoryId("")
    setImgUrl("")
    variationSetter([
      {
        title: "",
        storeProfit: "",
        janCode: "",
        titleError: null,
      }
    ])
    titleErrorSetter(null)
    categoryErrorSetter(null)
  }
  const onCompleted = data => {
    if (!props.food) clearFields()
    const message = props.food
      ? t("UpdateSuccessMessage")
      : t("AddSuccessMessage")
    mainErrorSetter("")
    successSetter(message)
    props.setModalLoading(false)
  }
  const onError = error => {
    console.log(error)
    mainErrorSetter(t("ErrorMessage"))
    successSetter("")
    props.setModalLoading(false)
  }
  const onDismiss = () => {
    successSetter("")
    mainErrorSetter("")
  }
  const imageToBase64 = imgUrl => {
    const fileReader = new FileReader()
    fileReader.onloadend = () => {
      setImgUrl(fileReader.result)
    }
    fileReader.readAsDataURL(imgUrl)
  }
  const SubmitButton = () => {
    const [mutate, { loading, error }] = useMutation(mutation, {
      onCompleted,
      onError,
      refetchQueries: [
        { query: getCategoryMasterQuery, variables: { id: props.ownerId } },
      ]
    })

    if (error) {
      console.log(error)
      return <p>{t("Error")}</p>
    }

    function onPressSubmit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
      e.preventDefault()
      if (getIsDisabled(userType) || !onSubmitValidaiton() || loading || props.modalLoading) return

      let variables
      let zeroFlag = false
      let minusFlag = false
      if (props.food) {
        variables = {
          oldCategoryId: props.food && props.food.categoryId ? props.food.categoryId : "",
          foodInput: {
            ownerId: props.ownerId,
            _id: props.food && props.food._id ? props.food._id : "",
            title: title.trim(),
            description: description.trim(),
            image: imgFile,
            categoryId,
            updateRestaurants: true,
            variations: variation.map(({ _id, title, storeProfit, janCode }) => {
              if (zenkakuToHankaku(storeProfit) === 0) zeroFlag = true
              if (zenkakuToHankaku(storeProfit) < 0) minusFlag = true
              return {
                _id: _id,
                title: title.trim(),
                storeProfit: zenkakuToHankaku(storeProfit),
                janCode: janCode.trim(),
              }
            })
          }
        }
      } else {
        variables = {
          foodInput: {
            ownerId: props.ownerId,
            title: title.trim(),
            description: description.trim(),
            image: imgFile,
            categoryId,
            updateRestaurants: true,
            variations: variation.map(({ title, storeProfit, janCode, }) => {
              if (zenkakuToHankaku(storeProfit) === 0) zeroFlag = true
              if (zenkakuToHankaku(storeProfit) < 0) minusFlag = true
              return {
                title: title.trim(),
                storeProfit: zenkakuToHankaku(storeProfit),
                janCode: janCode.trim(),
              }
            })
          }
        }
      }

      if (zeroFlag && !window.confirm("０円でもよろしいですか？")) {
        return
      }
      if (minusFlag) {
        window.alert("０円未満は入力できません")
        return
      }

      props.setModalLoading(true)
      mutate({ variables })
    }

    return <Button
      disabled={loading || props.modalLoading}
      color="primary"
      href="#pablo"
      className="btn-block"
      onClick={onPressSubmit}
      size="lg"
    >
      {loading || props.modalLoading ? t("Saving") : t("Save")}
    </Button>
  }

  const { data, loading, error } = useCategoryMasterQuery(props.ownerId)
  const SelectCategory = () => {
    if (loading) return <p>{t("Loading")}</p>
    if (error) return <p>{t("Error")}</p>

    return <FormGroup
      className={
        categoryError === null
          ? ""
          : categoryError
            ? "has-success"
            : "has-danger"
      }>
      <Input
        type="select"
        name="input-category"
        id="exampleSelect"
        value={categoryId}
        onChange={e => setCategoryId(e.target.value)}
      >
        {!categoryId && (
          <option value={""}>
            {t("Select")}
          </option>
        )}
        {data && data.categoryMasters && data.categoryMasters.map(
          category => (
            <option
              value={category._id}
              key={category._id}>
              {category.title}
            </option>
          )
        )}
      </Input>
    </FormGroup>
  }


  return (
    <>
      <Row>
        <Col className="order-xl-1">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">{props.food ? t("Edit") : t("Add")}</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <form ref={formRef}>
                <div className="pl-lg-4">
                  <Row>
                    <Col lg="5">
                      <Row>
                        <Col>
                          <label
                            className="form-control-label"
                            htmlFor="input-title">
                            {t("Title")}
                          </label>
                          <FormGroup
                            className={
                              titleError === null
                                ? ""
                                : titleError
                                  ? "has-success"
                                  : "has-danger"
                            }>
                            <Input
                              autoCapitalize="none"
                              className="form-control-alternative"
                              id="input-title"
                              name="input-title"
                              placeholder={t("FoodnamePH")}
                              type="text"
                              value={title}
                              onChange={e => setTitle(e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <label
                            className="form-control-label"
                            htmlFor="input-description">
                            {t("Description")}
                          </label>
                          <FormGroup>
                            <Input
                              className="form-control-alternative"
                              id="input-description"
                              name="input-description"
                              placeholder={t("FoodDescriptionPH")}
                              type="textarea"
                              value={description}
                              onChange={e => setDescription(e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <label
                            className="form-control-label"
                            htmlFor="input-category">
                            {t("Category")}
                          </label>
                          <SelectCategory />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <h3 className="mb-0"> {t("Food Image")}</h3>
                          <FormGroup>
                            <div className="card-title-image">
                              {imgUrl && typeof imgUrl === "string" && (
                                <a
                                  href="#pablo"
                                  onClick={e => e.preventDefault()}>
                                  <img
                                    alt="..."
                                    className="rounded-rectangle"
                                    src={imgUrl}
                                  />
                                </a>
                              )}
                              <input
                                className="mt-4"
                                type="file"
                                onChange={selectImage}
                              />
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>

                    <Col lg="7">
                      <h3 className="mb-2">{t("Variations")}</h3>
                      {variation.map((variationItem, variationIndex) => (
                        <div key={variationIndex}>
                          <Row>
                            <Col lg="6">
                              <FormGroup>
                                <label className="form-control-label" htmlFor="input-variation-title">
                                  {t("Title")}
                                </label>
                                <br />
                                <small style={{ color: "blue" }}>
                                  {t("Title must be unqiue")}
                                </small>
                                <Input
                                  autoCapitalize="none"
                                  className="form-control-alternative"
                                  value={variationItem.title}
                                  id="input-variation-title"
                                  placeholder={t("VariationsPH")}
                                  type="text"
                                  autoComplete="off"
                                  onChange={event => {
                                    handleVariationChange(
                                      event,
                                      variationIndex,
                                      "title",
                                    )
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col lg="6">
                              <FormGroup>
                                <label className="form-control-label" htmlFor="input-janCode">
                                  {t("JanCode")}
                                </label>
                                <Input
                                  autoCapitalize="none"
                                  className="form-control-alternative"
                                  value={variationItem.janCode}
                                  id="input-janCode"
                                  type="text"
                                  autoComplete="off"
                                  onChange={event => {
                                    handleVariationChange(
                                      event,
                                      variationIndex,
                                      "janCode",
                                    )
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col lg="6">
                              <FormGroup>
                                <label className="form-control-label" htmlFor="input-storeProfit">
                                  {t("StoreProfit")}
                                </label>
                                <Input
                                  className="form-control-alternative"
                                  value={variationItem.storeProfit}
                                  id="input-storeProfit"
                                  type="number"
                                  onChange={event => {
                                    handleVariationChange(
                                      event,
                                      variationIndex,
                                      "storeProfit",
                                    )
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row className="mt-2">
                            <Col lg="6">
                              <FormGroup>
                                <Button onClick={() => { onRemove(variationIndex) }} color="danger" > - </Button>
                                <Button onClick={() => { onAdd(variationIndex) }} color="primary" > + </Button>
                              </FormGroup>
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </Col>
                  </Row>
                  <hr />
                  <Row className="mt-2 justify-content-center">
                    <Col xs="4">
                      <SubmitButton />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <Alert
                        color="success"
                        isOpen={!!success}
                        toggle={onDismiss}>
                        <span className="alert-inner--icon">
                          <i className="ni ni-like-2" />
                        </span>{" "}
                        <span className="alert-inner--text">
                          <strong>{t("Success")}!</strong> {success}
                        </span>
                      </Alert>
                      <Alert
                        color="danger"
                        isOpen={!!mainError}
                        toggle={onDismiss}>
                        <span className="alert-inner--icon">
                          <i className="ni ni-like-2" />
                        </span>{" "}
                        <span className="alert-inner--text">
                          <strong>{t("Danger")}!</strong> {mainError}
                        </span>
                      </Alert>
                    </Col>
                  </Row>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}
export default FoodMasterModal
